"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import { KeyStream } from "@/components/tools/ciphers-polygraphic/key-stream";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { groupLetters } from "@/lib/tool-kit/ciphers/alphabet";
import {
  CUBE_SIDE,
  type TrifidBlock,
  type TrifidSymbol,
  trifidTransform,
} from "@/lib/tool-kit/ciphers-polygraphic/trifid";

const BLOCK_LIMIT = 4;
const MAX_PERIOD = 99;
const SYMBOLS: readonly TrifidSymbol[] = ["+", "#", "."];

/** English strings. A translated content file overrides these through `ui`. */
export const TRIFID_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Trifid is a fractionation cipher",
  keyword: "Keyword",
  keywordSample: "EXTRAORDINARY",
  keywordHint:
    "Its letters fill the cube first, each used once; the rest of the alphabet and the extra symbol follow in order, layer by layer.",
  symbol: "27th symbol",
  symbolHint:
    "A cube holds 27 symbols, so a 27th joins the 26 letters. Delastelle used +, the American Cryptogram Association uses #. It can be typed in the message like a letter.",
  period: "Period",
  periodHint:
    "Symbols per block, each enciphered on its own. Leave empty or 0 to treat the whole message as one block. A period that is not a multiple of 3 mixes the best.",
  periodSample: "10",
  group: "Cipher text in groups of five",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  letterNote:
    "Only the 26 letters and the 27th symbol are enciphered. J stays distinct from I. Spaces, digits and other punctuation are dropped, and the result is in capitals.",
  working: "Cube and blocks",
  cubeLabel: "Layer {layer} of the cube",
  cubeHint:
    "A cube is three layers of three rows and three columns, filled layer by layer. Every symbol has three coordinates — layer, row, column — each from 1 to 3.",
  ruleEncode:
    "Encoding: write each plain symbol's layer number in one line, its row number beneath and its column number beneath that. Read the digits across — all the layers, then all the rows, then all the columns — and cut them into triples. Each triple is the layer, row and column of a cipher symbol.",
  ruleDecode:
    "Decoding: turn each cipher symbol into its layer, row and column, and write the digits out in triples as one stream. The first third is the layers, the second the rows and the last the columns; going down the three lines gives the plain symbols.",
  blockTitle: "Block {number}",
  plainRow: "Plain",
  cipherRow: "Cipher",
  layerRow: "Layer",
  rowRow: "Row",
  columnRow: "Column",
  triplesRow: "Triples",
  streamLabel: "Digits across",
  blockLimit:
    "one: Showing the first block.|other: Showing the first {count} blocks.",
} as const;

type Strings = typeof TRIFID_CIPHER_STRINGS;

function triplesOf(stream: readonly number[]): string[] {
  const triples: string[] = [];
  for (let index = 0; index < stream.length; index += 3) {
    triples.push(`${stream[index]}${stream[index + 1]}${stream[index + 2]}`);
  }
  return triples;
}

function BlockWorking({
  block,
  number,
  encoding,
  strings,
}: {
  block: TrifidBlock;
  number: string;
  encoding: boolean;
  strings: Strings;
}) {
  const size = block.rows.length;
  const plain = (
    <KeyStream
      key="plain"
      rows={[
        { label: strings.plainRow, letters: [...block.plain] },
        { label: strings.layerRow, letters: block.layers.map(String) },
        { label: strings.rowRow, letters: block.rows.map(String) },
        { label: strings.columnRow, letters: block.columns.map(String) },
      ]}
      shaded={0}
      title={strings.plainRow}
    />
  );
  const cipher = (
    <KeyStream
      key="cipher"
      rows={[
        { label: strings.triplesRow, letters: triplesOf(block.stream) },
        { label: strings.cipherRow, letters: [...block.cipher] },
      ]}
      shaded={0}
      title={strings.cipherRow}
    />
  );
  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <p className={LABEL}>{t(strings.blockTitle, { number })}</p>
      {encoding ? [plain, cipher] : [cipher, plain]}
      <p className={HINT}>
        {strings.streamLabel}:{" "}
        <span className="font-mono text-foreground">
          {block.stream.slice(0, size).join("")}{" "}
          {block.stream.slice(size, size * 2).join("")}{" "}
          {block.stream.slice(size * 2).join("")}
        </span>
      </p>
    </div>
  );
}

export default function TrifidCipherWidget() {
  const strings = useToolUi(TRIFID_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [symbol, setSymbol] = useState<TrifidSymbol>("#");
  const [periodText, setPeriodText] = useState<string>(strings.periodSample);
  const [group, setGroup] = useState(true);

  const encoding = mode === "encode";
  const parsed = parseIntegerInput(periodText);
  const period = Math.min(Math.max(parsed ?? 0, 0), MAX_PERIOD);
  const result = trifidTransform(input, { keyword, symbol, period }, mode);
  const output =
    group && encoding ? groupLetters(result.output, 5) : result.output;
  const shown = result.blocks.slice(0, BLOCK_LIMIT);
  const face = CUBE_SIDE * CUBE_SIDE;
  const headers = ["1", "2", "3"];

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.keywordHint}
          id="tf-keyword"
          label={strings.keyword}
          mono
          onChange={setKeyword}
          value={keyword}
        />
        <TextField
          hint={strings.periodHint}
          id="tf-period"
          label={strings.period}
          mono
          numeric
          onChange={setPeriodText}
          value={periodText}
        />
      </div>
      <SelectField
        hint={strings.symbolHint}
        id="tf-symbol"
        label={strings.symbol}
        onChange={setSymbol}
        options={SYMBOLS.map((value) => ({ value, label: value }))}
        value={symbol}
      />
      {encoding ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  const working = (
    <>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {encoding ? strings.ruleEncode : strings.ruleDecode}
      </p>
      <div className="space-y-2">
        <div className="grid gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((layer) => {
            const label = t(strings.cubeLabel, { layer: fmt(layer + 1) });
            return (
              <div className="space-y-1" key={layer}>
                <p className={LABEL}>{label}</p>
                <CipherGrid
                  cells={[
                    ...result.cube.slice(layer * face, (layer + 1) * face),
                  ]}
                  columnHeaders={headers}
                  columns={CUBE_SIDE}
                  label={label}
                  rowHeaders={headers}
                />
              </div>
            );
          })}
        </div>
        <p className={HINT}>{strings.cubeHint}</p>
      </div>
      {shown.map((block, index) => (
        <BlockWorking
          block={block}
          encoding={encoding}
          // biome-ignore lint/suspicious/noArrayIndexKey: blocks are positional
          key={index}
          number={fmt(index + 1)}
          strings={strings}
        />
      ))}
      {result.blocks.length > BLOCK_LIMIT ? (
        <p className={HINT}>
          {tPlural(strings.blockLimit, locale, BLOCK_LIMIT, {
            count: fmt(BLOCK_LIMIT),
          })}
        </p>
      ) : null}
    </>
  );

  return (
    <CipherWorkbench
      idPrefix="tf"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      outputNotes={<p className={HINT}>{strings.letterNote}</p>}
      working={working}
    />
  );
}
